/**
 * ScopeBar — top context strip (Milestone 2.6, spec §11).
 *
 * Desktop (md+), founder spec 2026-06-11: REMOVED. The old
 * [OrgSwitcher] / [ProjectSwitcher] / [BoardTabs] strip was redundant —
 * project/board navigation already lives in the left sidebar tree, and
 * the board name + Kanban/List view switcher live in the board
 * page header. The component now returns null on desktop so the kanban
 * starts higher. The CEO orchestrator chat moved to the bottom of the
 * left ActivityBar rail.
 *
 * Mobile (<md), founder spec 2026-05-25 (Wave-D, top-bar refactor):
 *   [☰ burger]  [Board name · view-switcher chips]  [CEO chat]
 *
 *   The mobile bar collapses to ONE row. The middle slot shows the
 *   current board name in plain title text (no pill chrome) with the
 *   Kanban/List chips inline immediately after, controlled via
 *   the `?view=` query param on `/board/:boardId`. Off a board route
 *   (e.g. /boards, /inbox, /files) the middle slot collapses to the
 *   project · board scope pill so the user can still navigate scope.
 *
 *   Previous mobile design (ScopePill always centred) is preserved as
 *   the non-board fallback. The board-switcher pill that used to sit
 *   immediately right of the burger is gone.
 *
 * Renders only when the user has membership in the active org. Looks
 * sensible even when project/board are empty (those subwidgets handle
 * their own empty/loading states).
 *
 * MOUNTED BY <Shell>, not by route files (2026-08-01, QA D-1/D-2/D-3). It used
 * to be mounted by `/board/$boardId` and `/inbox` only, which meant a phone had
 * no burger — and therefore no Workspace switcher and no account menu — on
 * every other route, Settings included.
 */
import type { ViewType } from '@chaos/types/views';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Check, Columns3, FoldHorizontal, List, Menu, X } from 'lucide-react';
import * as React from 'react';

import { useActiveOrg } from '../../lib/active-org';
import { useActiveProject } from '../../lib/active-project';
import { boardsApi, boardsKeys } from '../../lib/api/boards';
import { kanbanApi, kanbanKeys } from '../../lib/api/kanban';
import { projectsV2Api, projectsV2Keys } from '../../lib/api/projects-v2';
import { viewsApi, viewsKeys } from '../../lib/api/views';
import { useHideEmptyColumns } from '../../lib/kanban/use-collapsed-columns';
import { useIsMobile } from '../../lib/use-media-query';
import { useAuthStore } from '../../stores/auth-store';
import { cn } from '../../lib/utils';
import { env } from '../../env';
import { PrivatePortal } from '../ui/private-portal';
// 2026-06-11: BoardTabs + ProjectSwitcher were desktop-only (the
// scope-switcher strip). The desktop strip is gone, so these are no
// longer imported. OrgSwitcher / ScopePill / CeoChat are still used by
// the mobile bar below.
import { CeoChat } from './ceo-chat';
import { OrgSwitcher } from './org-switcher';
import { SCOPE_SHEET_ROW_CLASS, ScopePill } from './scope-pill';
import { useMobileDrawer } from './mobile-drawer-context';
import type { SwitchableView } from '../views/view-switcher';

const VIEW_OPTIONS: ReadonlyArray<{ value: SwitchableView; label: string; icon: typeof Columns3 }> = [
  { value: 'kanban', label: 'Kanban', icon: Columns3 },
  { value: 'list', label: 'List', icon: List },
];

export function ScopeBar({ className }: { className?: string }) {
  const { activeOrgId, orgs } = useActiveOrg();
  const { activeBoardId } = useActiveProject();
  const isMember = activeOrgId !== null && orgs.some((o) => o.id === activeOrgId);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const isMobile = useIsMobile();
  const { setOpen: setDrawerOpen } = useMobileDrawer();
  const params = useParams({ strict: false }) as { boardId?: string };
  const search = useSearch({ strict: false }) as { view?: ViewType };
  const navigate = useNavigate();
  const qc = useQueryClient();

  const onBoardRoute = !!params.boardId;
  const currentBoardId = params.boardId ?? activeBoardId ?? null;

  // Board name — pulled directly from the kanban board cache so we
  // don't depend on the board route having loaded it yet. Cheap;
  // useQuery just returns whatever the route already populated.
  const boardQ = useQuery({
    queryKey: currentBoardId ? kanbanKeys.board(currentBoardId) : ['kanban', 'board', '_none'],
    queryFn: () => (currentBoardId ? kanbanApi.getBoard(currentBoardId) : Promise.resolve(null)),
    enabled: !!currentBoardId && isMobile,
    staleTime: 30_000,
  });

  // Persisted view preference — only needed when we have to compute
  // the effective view on mobile. Same shape as board route.
  const prefQ = useQuery({
    queryKey: currentBoardId ? viewsKeys.preference(currentBoardId) : ['views', 'pref', '_none'],
    queryFn: () => (currentBoardId ? viewsApi.getPreference(currentBoardId) : Promise.resolve(null)),
    enabled: !!currentBoardId && isMobile,
    staleTime: 60_000,
  });

  const view: ViewType = search.view ?? prefQ.data?.viewType ?? 'kanban';

  const upsertView = useMutation({
    mutationFn: (next: ViewType) =>
      currentBoardId
        ? viewsApi.upsertPreference(currentBoardId, { viewType: next })
        : Promise.resolve(null),
    onSuccess: (preference) => {
      if (preference && currentBoardId) {
        qc.setQueryData(viewsKeys.preference(currentBoardId), preference);
      }
    },
  });

  function setView(next: SwitchableView) {
    if (!currentBoardId) return;
    void navigate({
      to: '/board/$boardId',
      params: { boardId: currentBoardId },
      search: (prev) => ({ ...(prev as object), view: next }),
      replace: true,
    });
    if (prefQ.data?.viewType !== next) {
      upsertView.mutate(next);
    }
  }

  if (isMobile) {
    return (
      <div
        data-testid="scope-bar"
        className={cn(
          'flex flex-col border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/75',
          // pt-safe for the notch + px-safe so the burger / first control clear
          // the notch & home-indicator corner radius in landscape (design-bug D4).
          'pt-safe px-safe',
          className,
        )}
      >
        <div className="flex w-full min-w-0 items-center gap-1.5 px-2 py-2">
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setDrawerOpen(true)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-foreground transition-colors hover:bg-foreground/5 active:bg-foreground/10"
            data-testid="mobile-burger"
          >
            <Menu className="h-5 w-5" aria-hidden />
          </button>

          {onBoardRoute && isMember ? (
            <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
              {/* Board name as plain title text. Tapping opens the FULL
                  org / project / board scope sheet (not the old flat, often-
                  empty board list). Single line, truncated. */}
              <MobileBoardTitle
                title={boardQ.data?.name ?? '—'}
                boardProjectId={boardQ.data?.projectId ?? null}
                currentBoardId={currentBoardId}
              />
              {/* View switcher chips — inline with the title.
                  The buttons stay reachable but never push the title
                  off-screen because the title gets min-w-0. */}
              <div
                role="tablist"
                aria-label="Board view"
                className="flex shrink-0 items-center gap-0.5"
                data-testid="mobile-view-switcher"
              >
                {VIEW_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const active = view === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      aria-label={opt.label}
                      onClick={() => setView(opt.value)}
                      className={cn(
                        'inline-flex h-8 w-8 items-center justify-center rounded transition-colors',
                        active
                          ? 'bg-foreground/[0.08] text-foreground'
                          : 'text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground',
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  );
                })}
              </div>
              {/* R5 auto-collapse-empty toggle — the desktop board header hosts
                  this button, but that header is hidden on mobile, so surface it
                  here (Kanban only, gated by VITE_DESIGN_LIGHT like desktop).
                  Shares the per-board hideEmpty store with the kanban, so it
                  folds every 0-card status into its rail. */}
              {env.VITE_DESIGN_LIGHT && view === 'kanban' && currentBoardId && (
                <MobileHideEmptyToggle boardId={currentBoardId} />
              )}
            </div>
          ) : isMember ? (
            <ScopePill className="flex-1" />
          ) : (
            <div className="flex-1">
              <OrgSwitcher />
            </div>
          )}

          {userId && (
            <div className="shrink-0">
              <CeoChat userId={userId} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop — the top scope-switcher strip was removed (founder spec
  // 2026-06-11). Project / board navigation already lives in the left
  // sidebar tree, so the Org / Project / BoardTabs row was redundant
  // chrome stealing vertical space above the kanban. The board name +
  // view switcher (Kanban / List) live in the board page's own
  // header (board/$boardId.lazy.tsx), so nothing functional is lost
  // here. The CEO orchestrator chat moved to the BOTTOM of the left
  // ActivityBar rail (components/layout/activity-bar.tsx).
  //
  // Rendering nothing on desktop reclaims the vertical space so the
  // kanban starts higher. The MOBILE branch above (burger + ScopePill /
  // board title + view chips + CEO chat) is the essential mobile nav
  // and stays fully intact.
  return null;
}

// ── Mobile hide-empty-columns toggle (Kanban only) ────────────────────
//
// Desktop hosts the R5 auto-collapse-empty button in the board-page header
// (board/$boardId.lazy.tsx → HideEmptyToggle), but that whole header is hidden
// on mobile — so the button was unreachable on phones. We render an equivalent
// here in the mobile ScopeBar. It shares the SAME per-board `hideEmpty` store
// (localStorage + CustomEvent), so toggling it folds every 0-card status into
// its rail on the kanban and stays in sync with the desktop control.
function MobileHideEmptyToggle({ boardId }: { boardId: string }) {
  const { hideEmpty, toggleHideEmpty } = useHideEmptyColumns(boardId);
  const label = hideEmpty ? 'Showing all columns' : 'Hide empty columns';
  return (
    <button
      type="button"
      onClick={toggleHideEmpty}
      aria-pressed={hideEmpty}
      aria-label={label}
      title={label}
      className={cn(
        // 32px visual to match the view chips, with an invisible ≥44px tap area
        // grown on coarse pointers (phones) via an inset ::after.
        'relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded transition-colors',
        "pointer-coarse:after:absolute pointer-coarse:after:-inset-2 pointer-coarse:after:content-['']",
        hideEmpty
          ? 'bg-foreground/[0.08] text-foreground'
          : 'text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground',
      )}
      data-testid="mobile-hide-empty-toggle"
    >
      <FoldHorizontal className="h-3.5 w-3.5" aria-hidden />
      <span className="sr-only">{label}</span>
    </button>
  );
}

// ── Mobile board title (plain text, tap → full scope bottom sheet) ────────
//
// Tapping the board name opens a FULL org / project / board switcher — the same
// three-section sheet as <ScopePill>, so the user gets the whole scope, not a
// flat board list. Root-cause fix (founder 2026-07-12): the OLD sheet listed
// boards scoped to the active-project store, which on a board route is often
// null / stale / pointing at a DIFFERENT project than the board you're viewing,
// so it came back "No boards in this project." We now SEED the board scope from
// the CURRENT board's own project (`boardProjectId`), so the list is always the
// siblings of the board you're on; switching a project row re-scopes live.

interface MobileBoardTitleProps {
  title: string;
  boardProjectId: string | null;
  currentBoardId: string | null;
}

function MobileBoardTitle({ title, boardProjectId, currentBoardId }: MobileBoardTitleProps) {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const { orgs, activeOrgId, setActiveOrg } = useActiveOrg();
  const { activeProjectId, setActiveProject, setActiveBoard } = useActiveProject();
  const isMember = activeOrgId !== null && orgs.some((o) => o.id === activeOrgId);

  // Which project's boards to show. Default to the CURRENT board's project (the
  // fix), then any project the user picks in the sheet, then the active-project
  // store as a last resort.
  const [sheetProjectId, setSheetProjectId] = React.useState<string | null>(null);
  const scopedProjectId = sheetProjectId ?? boardProjectId ?? activeProjectId;

  // Lock body scroll while sheet is open — same pattern as ScopePill.
  React.useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  const projectsQ = useQuery({
    queryKey: activeOrgId ? projectsV2Keys.list(activeOrgId) : ['projects-v2', 'list', '_none'],
    queryFn: () => (activeOrgId ? projectsV2Api.list(activeOrgId) : Promise.resolve([])),
    enabled: open && !!activeOrgId && isMember,
    staleTime: 30_000,
  });
  const projects = projectsQ.data ?? [];

  const boardsQ = useQuery({
    queryKey:
      activeOrgId && scopedProjectId
        ? boardsKeys.listInProject(activeOrgId, scopedProjectId)
        : ['boards-v2', 'list', '_none'],
    queryFn: () =>
      activeOrgId && scopedProjectId
        ? boardsApi.list(activeOrgId, scopedProjectId)
        : Promise.resolve([]),
    enabled: open && !!activeOrgId && !!scopedProjectId && isMember,
    staleTime: 30_000,
  });
  const boards = boardsQ.data ?? [];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Switch scope (current board: ${title})`}
        className={cn(
          // Founder 2026-07-12: normal system sans (like the sidebar), not the
          // Bricolage display face.
          'min-w-0 flex-1 truncate text-left text-[15px] font-semibold tracking-tight text-foreground transition-colors hover:text-muted-foreground',
        )}
        data-testid="mobile-board-title"
      >
        {title}
      </button>

      {/* Portalled to the private root — the bar's own `backdrop-blur` is a containing
          block for `position: fixed`, so an in-place sheet laid its rows out
          above the top of the screen. Same defect and same fix as ScopePill;
          see the note in scope-pill.tsx. */}
      {open && (
        <PrivatePortal>
          <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true">
          <button
            type="button"
            aria-label="Close"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-foreground/50 backdrop-blur-sm"
          />
          <div
            className={cn(
              'absolute inset-x-0 bottom-0 max-h-[85dvh] overflow-y-auto rounded-t-2xl bg-background shadow-2xl',
              'animate-slide-up pb-safe',
            )}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background px-4 py-3">
              <h2 className="text-[15px] font-semibold tracking-tight text-foreground">Switch board</h2>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            {/* Organizations — only when the user has more than one, so the
                common single-org case stays a short Projects + Boards sheet. */}
            {orgs.length > 1 && (
              <ScopeSheetSection title="Workspaces">
                {orgs.map((o) => (
                  <ScopeSheetRow
                    key={o.id}
                    label={o.displayName}
                    icon={o.icon ?? null}
                    isActive={o.id === activeOrgId}
                    onSelect={() => {
                      setActiveOrg(o.id);
                      // Let the boards scope fall back to the new org's active
                      // project until the user drills in.
                      setSheetProjectId(null);
                    }}
                  />
                ))}
              </ScopeSheetSection>
            )}

            {/* Projects */}
            <ScopeSheetSection title="Projects">
              {projects.length === 0 ? (
                <ScopeSheetEmpty>No projects in this Workspace.</ScopeSheetEmpty>
              ) : (
                projects.map((p) => (
                  <ScopeSheetRow
                    key={p.id}
                    label={p.displayName}
                    icon={p.icon ?? null}
                    isActive={p.id === scopedProjectId}
                    onSelect={() => {
                      setSheetProjectId(p.id);
                      setActiveProject(p.id);
                    }}
                  />
                ))
              )}
            </ScopeSheetSection>

            {/* Boards */}
            <ScopeSheetSection title="Boards">
              {boards.length === 0 ? (
                <ScopeSheetEmpty>No boards in this project.</ScopeSheetEmpty>
              ) : (
                boards.map((b) => (
                  <ScopeSheetRow
                    key={b.id}
                    label={b.displayName}
                    icon={b.icon ?? null}
                    isActive={b.id === currentBoardId}
                    onSelect={() => {
                      setActiveBoard(b.id);
                      void navigate({ to: '/board/$boardId', params: { boardId: b.id } });
                      setOpen(false);
                    }}
                  />
                ))
              )}
            </ScopeSheetSection>
          </div>
          </div>
        </PrivatePortal>
      )}
    </>
  );
}

// ── Scope-sheet primitives (mirror ScopePill's private helpers) ───────────
function ScopeSheetSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-border last:border-b-0">
      <div className="px-4 pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      <div className="pb-1">{children}</div>
    </div>
  );
}

function ScopeSheetRow({
  label,
  icon,
  isActive,
  onSelect,
}: {
  label: string;
  icon?: string | null;
  isActive: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        SCOPE_SHEET_ROW_CLASS,
        isActive
          ? 'bg-foreground/[0.06] text-foreground'
          : 'text-foreground hover:bg-foreground/5 active:bg-foreground/10',
      )}
    >
      {icon && (
        <span aria-hidden className="text-base leading-none">
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate text-[14px] font-medium">{label}</span>
      {isActive && <Check className="h-4 w-4 shrink-0 text-foreground" aria-hidden />}
    </button>
  );
}

function ScopeSheetEmpty({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-3 text-[12px] text-muted-foreground">{children}</div>;
}
